import { createApi } from '@reduxjs/toolkit/query/react';

import { IRtcStats } from 'vcs-common';

import { IClient, IReferal, IReferalUser, IUser } from '../store/types';

import { baseQueryWithReauth } from './api';

export {
  useLazyGetConsultationQuery,
  useLazyGetConsultationByCodeQuery,
  useLazyGetConsultationsQuery,
  useLazyGetAllConsultationsQuery,
  useCreateConsultationMutation,
  useChangeConsultationMutation,
  useGetConsultationQuery,
  useEditConsultationMutation,
  useGetConsultationsQuery,
  useDeleteConsultationMutation,
  useUpdateConsultationMutation,
  useGenerateVideoRoomTokenMutation,
} from './consultations.api';

export const usersApi = createApi({
  reducerPath: 'user',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Users', 'Referals'],
  endpoints: (build) => ({
    me: build.query<IUser, void>({
      query: () => ({
        url: '/users/me',
      }),
    }),
    uploadMetrics: build.mutation<void, IRtcStats>({
      query: (data) => ({
        url: '/victoria_data/client',
        method: 'POST',
        body: JSON.stringify(data),
      }),
    }),
    getClient: build.query<IClient, string>({
      providesTags: (result, error) => ['User'],
      query: (id) => ({
        url: `/clients/${id}`,
      }),
    }),
    getClients: build.query<IClient[], { sort?: 'ASC' | 'DESC'; is_active?: boolean; full_name?: string }>({
      providesTags: (result, error) => ['Users'],
      query: (params) => ({
        url: '/clients',
        params: {
          itemsPerPage: 500,
          is_active: params.is_active,
          full_name: params.full_name !== '' ? params.full_name : undefined,
          'sort[id]': params.sort,
        },
      }),
    }),
    changeUser: build.mutation<IUser, Partial<IUser>>({
      query: (data) => ({
        url: `/users/${data.id}`,
        method: 'PUT',
        body: JSON.stringify(data),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['User', 'Users']),
    }),
    changeClient: build.mutation<IClient, Partial<IClient>>({
      query: (data) => ({
        url: `/clients/${data.id}`,
        method: 'PUT',
        body: JSON.stringify(data),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['User', 'Users']),
    }),
    createClient: build.mutation<
      IClient,
      Pick<IClient, 'full_name' | 'is_active' | 'time_zone' | 'psychologists'> & { is_group?: boolean }
    >({
      query: (body) => ({
        url: '/clients',
        method: 'POST',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['User', 'Users']),
    }),
    getReferalCodes: build.query<IReferal, void>({
      query: () => '/referal-codes',
      providesTags: ['Referals'],
    }),
    getReferalUsers: build.query<{ users: IReferalUser[] }, void>({
      query: () => '/referal-codes/stats',
    }),
    createReferalCode: build.mutation<void, void>({
      query: () => ({
        url: '/referal-codes',
        method: 'POST',
        body: JSON.stringify({ type: 'registration' }),
      }),
      invalidatesTags: ['Referals'],
    }),
  }),
});

export const {
  useLazyMeQuery,
  useLazyGetClientQuery,
  useLazyGetClientsQuery,
  useChangeUserMutation,
  useChangeClientMutation,
  useGetClientQuery,
  useCreateClientMutation,
  useGetClientsQuery,
  useGetReferalCodesQuery,
  useGetReferalUsersQuery,
  useCreateReferalCodeMutation,
  useUploadMetricsMutation,
} = usersApi;
